import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    TextField,
    InputAdornment,
    ListSubheader,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import CutOffQuery from '../../../services/query/cutOff/CutOffQuery.services';
import { COSK } from '../cutOff.classes';
import { loadCutOffFilterOptions } from '../cutOffFilterOptions.helpers';
import {
    ActiveStatusEnum,
    buildSearchFilterParams,
    formatDeliveryTypeSelectValue,
    nextSelectWithAllValue,
} from '../cutOffSearchDrawer.helpers';

interface SearchDrawerProps {
    isDrawerOpen: boolean;
    handleDrawerClose: () => void;
    handleSearch: (filterParams: any) => void;
    cargoCompanyOptions?: { id: number; name: string }[];
}

export const SearchDrawer = ({
    isDrawerOpen,
    handleDrawerClose,
    handleSearch,
    cargoCompanyOptions: cargoCompanyOptionsProp = [],
}: SearchDrawerProps) => {
    const { t } = useTranslation();
    const ALL_OPTION_ID = -1;

    // Active status options with translated names
    const activeStatusOptions = useMemo(
        () => [
            { id: ActiveStatusEnum.ACTIVE, name: t('general.active') ?? 'Active' },
            { id: ActiveStatusEnum.PASSIVE, name: t('general.passive') ?? 'Passive' },
        ],
        [t]
    );

    const initialFormState: any = useMemo(
        () => ({
            selectedCargoCompany: [ALL_OPTION_ID],
            selectedDistance: [],
            selectedDeliveryType: [],
            selectedDepartureLocation: [],
            selectedArrivalLocation: [],
            selectedActiveStatus: [],
        }),
        []
    );

    const [formState, setFormState] = useState<any>(initialFormState);
    const [cargoCompanyOptions, setCargoCompanyOptions] =
        useState<{ id: number; name: string }[]>(cargoCompanyOptionsProp);
    const [distanceOptions, setDistanceOptions] = useState<{ id: number; name: string }[]>([]);
    const [deliveryTypeOptions, setDeliveryTypeOptions] = useState<{ id: number; name: string }[]>([]);
    const [departureLocationOptions, setDepartureLocationOptions] = useState<{ id: number; name: string }[]>([]);
    const [arrivalLocationOptions, setArrivalLocationOptions] = useState<{ id: number; name: string }[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    // Search states for each select
    const [searchCargoCompany, setSearchCargoCompany] = useState('');
    const [searchDistance, setSearchDistance] = useState('');
    const [searchDeliveryType, setSearchDeliveryType] = useState('');
    const [searchDepartureLocation, setSearchDepartureLocation] = useState('');
    const [searchArrivalLocation, setSearchArrivalLocation] = useState('');
    const [searchActiveStatus, setSearchActiveStatus] = useState('');

    // Update cargoCompanyOptions when prop changes
    useEffect(() => {
        if (cargoCompanyOptionsProp?.length > 0) {
            setCargoCompanyOptions(cargoCompanyOptionsProp);
        }
    }, [cargoCompanyOptionsProp]);

    // Fetch filter options on first drawer open (only fetch APIs for empty options)
    useEffect(() => {
        const needsCargoCompanies = cargoCompanyOptionsProp?.length === 0 && cargoCompanyOptions?.length === 0;
        const needsDistanceRanges = distanceOptions?.length === 0;
        const needsDeliveryTypes = deliveryTypeOptions?.length === 0;
        const needsDepartureOptions = departureLocationOptions?.length === 0;
        const needsArrivalOptions = arrivalLocationOptions?.length === 0;
        const needsLocationOptions = needsDepartureOptions || needsArrivalOptions;

        if (
            !isDrawerOpen ||
            (!needsCargoCompanies &&
                !needsDistanceRanges &&
                !needsDeliveryTypes &&
                !needsLocationOptions) ||
            isLoadingOptions
        ) {
            return;
        }

        setIsLoadingOptions(true);
        loadCutOffFilterOptions(
            {
                needsCargoCompanies,
                needsDistanceRanges,
                needsDeliveryTypes,
                needsDepartureOptions,
                needsArrivalOptions,
            },
            {
                setCargoCompanyOptions,
                setDistanceOptions,
                setDeliveryTypeOptions,
                setDepartureLocationOptions,
                setArrivalLocationOptions,
            }
        )
            .catch((err: any) => {
                console.error('Error fetching filter options:', err);
            })
            .finally(() => {
                setIsLoadingOptions(false);
            });
    }, [isDrawerOpen]);

    // Auto-set distance when exactly one departure and one arrival location are selected
    useEffect(() => {
        let isCancelled = false;

        const departureIds = (formState?.selectedDepartureLocation ?? []).filter(
            (id: number) => id !== ALL_OPTION_ID
        );
        const arrivalIds = (formState?.selectedArrivalLocation ?? []).filter((id: number) => id !== ALL_OPTION_ID);

        if (departureIds.length !== 1 || arrivalIds.length !== 1) {
            return;
        }

        CutOffQuery.GetLocationDistanceMatrix({
            departureLocationId: departureIds[0],
            arrivalLocationId: arrivalIds[0],
        })
            .then((res: any) => {
                if (isCancelled) return;

                if (res?.isSuccess && res?.distanceRangeId != null) {
                    setFormState((prev: any) => ({
                        ...prev,
                        selectedDistance: [res.distanceRangeId],
                    }));
                }
            })
            .catch((err: any) => {
                if (!isCancelled) {
                    console.error('Error fetching location distance matrix:', err);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [formState?.selectedDepartureLocation, formState?.selectedArrivalLocation]);

    /**
     * Generic handler for select with "All" option
     * When "All" is selected, deselect all others
     * When another option is selected while "All" is active, remove "All"
     */
    const handleSelectWithAllChange = (field: string) => (event: any) => {
        const value = event?.target?.value ?? [];
        const previousValue = formState?.[field] ?? [];
        const nextValue = nextSelectWithAllValue(value, previousValue, ALL_OPTION_ID);

        setFormState((prev: any) => ({
            ...prev,
            [field]: nextValue,
        }));
    };

    // Check if "All" is selected for each field
    const isAllSelectedForField = (field: string) => formState?.[field]?.includes(ALL_OPTION_ID) ?? false;

    /**
     * Generic render value function that handles "All" option
     */
    const renderSelectValueWithAll = (
        selected: any[],
        options: { id: any; name: string }[],
        nameKey: string = 'name'
    ) => {
        if (!selected?.length) return '';
        if (selected.includes(ALL_OPTION_ID)) {
            return t('general.all');
        }
        return (options ?? [])
            .filter((opt: any) => opt?.id != null && selected.includes(opt.id))
            .map((opt: any) => opt?.[nameKey] ?? '')
            .join(', ');
    };

    // Generic filter helper with locale support (especially for Turkish İ/i case conversion)
    const filterOptions = <T,>(options: T[], search: string, getLabel: (opt: T) => string | undefined): T[] => {
        if (!search?.trim()) return options;
        const locale = localStorage.getItem('lan') || 'tr-TR';
        const searchLower = search.toLocaleLowerCase(locale);
        return options.filter((opt) => getLabel(opt)?.toLocaleLowerCase(locale)?.includes(searchLower));
    };

    const filteredCargoCompanyOptions = useMemo(
        () => filterOptions(cargoCompanyOptions ?? [], searchCargoCompany, (opt) => opt?.name),
        [cargoCompanyOptions, searchCargoCompany]
    );
    const filteredDistanceOptions = useMemo(
        () => filterOptions(distanceOptions ?? [], searchDistance, (opt) => opt?.name),
        [distanceOptions, searchDistance]
    );
    const filteredDeliveryTypeOptions = useMemo(
        () => filterOptions(deliveryTypeOptions ?? [], searchDeliveryType, (opt: any) => opt?.deliveryTypeTranslated),
        [deliveryTypeOptions, searchDeliveryType]
    );
    const filteredDepartureLocationOptions = useMemo(
        () => filterOptions(departureLocationOptions ?? [], searchDepartureLocation, (opt) => opt?.name),
        [departureLocationOptions, searchDepartureLocation]
    );
    const filteredArrivalLocationOptions = useMemo(
        () => filterOptions(arrivalLocationOptions ?? [], searchArrivalLocation, (opt) => opt?.name),
        [arrivalLocationOptions, searchArrivalLocation]
    );
    const filteredActiveStatusOptions = useMemo(
        () => filterOptions(activeStatusOptions ?? [], searchActiveStatus, (opt) => opt?.name),
        [activeStatusOptions, searchActiveStatus]
    );

    /**
     * Handle filter button click
     * Transforms formState into API-ready filter params
     * NOTE: If "All" is selected OR both Active and Passive are selected for activeStatus, send null (meaning no filter)
     */
    const handleFilter = () => {
        handleSearch(buildSearchFilterParams(formState, ALL_OPTION_ID));
    };

    // Search input component - Using ListSubheader to prevent it from being treated as a selectable option
    const renderSearchInput = (searchValue: string, setSearchValue: (val: string) => void) => (
        <ListSubheader sx={{ p: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
            <TextField
                className={COSK.inputMenuSearch}
                size="small"
                fullWidth
                autoFocus
                placeholder={t('general.search') ?? 'Search...'}
                value={searchValue}
                onChange={(e) => setSearchValue(e?.target?.value ?? '')}
                onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                        e.stopPropagation();
                    }
                }}
                slotProps={{
                    htmlInput: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    },
                }}
            />
        </ListSubheader>
    );

    return (
        <SCUIGeneralDrawer width="480px" open={isDrawerOpen} onClose={handleDrawerClose}>
            <SCUIGeneralDrawerHeader header={t('cutOff.searchDrawerTitle')} onClose={handleDrawerClose} />
            {isLoadingOptions && <SCUILoadingLinear display={isLoadingOptions} />}
            <Box className={COSK.drawerBody} sx={{ padding: '16px', display: 'flex', flexDirection: 'column' }}>
                {/* Form Container */}
                <Box className={COSK.formFields} sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Cargo Company */}
                    <FormControl className={COSK.fieldCargoCompany} fullWidth>
                        <InputLabel id="cargoCompany-label">
                            {t('cutOff.cargoCompany')}
                            {` (*)`}
                        </InputLabel>
                        <Select
                            className={COSK.inputSelectCargoCompany}
                            labelId="cargoCompany-label"
                            multiple
                            value={formState?.selectedCargoCompany ?? []}
                            onChange={handleSelectWithAllChange('selectedCargoCompany')}
                            renderValue={(selected) => renderSelectValueWithAll(selected, cargoCompanyOptions)}
                            label={t('cutOff.cargoCompany')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchCargoCompany('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchCargoCompany, setSearchCargoCompany)}
                            {/* Select All Option */}
                            <MenuItem key={ALL_OPTION_ID} value={ALL_OPTION_ID}>
                                <Checkbox checked={isAllSelectedForField('selectedCargoCompany')} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {/* Individual Options */}
                            {filteredCargoCompanyOptions.map((option) => (
                                <MenuItem
                                    key={option?.id}
                                    value={option?.id}
                                    disabled={isAllSelectedForField('selectedCargoCompany')}
                                >
                                    <Checkbox
                                        checked={formState?.selectedCargoCompany?.includes(option?.id) ?? false}
                                        disabled={isAllSelectedForField('selectedCargoCompany')}
                                    />
                                    <ListItemText primary={option?.name ?? ''} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Distance */}
                    <FormControl className={COSK.fieldDistance} fullWidth>
                        <InputLabel id="distance-label">{t('cutOff.distance')}</InputLabel>
                        <Select
                            className={COSK.inputSelectDistance}
                            labelId="distance-label"
                            multiple
                            value={formState?.selectedDistance ?? []}
                            onChange={handleSelectWithAllChange('selectedDistance')}
                            renderValue={(selected) => renderSelectValueWithAll(selected, distanceOptions)}
                            label={t('cutOff.distance')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchDistance('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchDistance, setSearchDistance)}
                            {/* Select All Option */}
                            <MenuItem key={ALL_OPTION_ID} value={ALL_OPTION_ID}>
                                <Checkbox checked={isAllSelectedForField('selectedDistance')} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {filteredDistanceOptions.map((option) => (
                                <MenuItem
                                    key={option?.id}
                                    value={option?.id}
                                    disabled={isAllSelectedForField('selectedDistance')}
                                >
                                    <Checkbox
                                        checked={formState?.selectedDistance?.includes(option?.id) ?? false}
                                        disabled={isAllSelectedForField('selectedDistance')}
                                    />
                                    <ListItemText primary={option?.name ?? ''} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Delivery Type */}
                    <FormControl className={COSK.fieldDeliveryType} fullWidth>
                        <InputLabel id="deliveryType-label">{t('cutOff.deliveryType')}</InputLabel>
                        <Select
                            className={COSK.inputSelectDeliveryType}
                            labelId="deliveryType-label"
                            multiple
                            value={formState?.selectedDeliveryType ?? []}
                            onChange={handleSelectWithAllChange('selectedDeliveryType')}
                            renderValue={(selected) =>
                                formatDeliveryTypeSelectValue(
                                    selected,
                                    deliveryTypeOptions,
                                    ALL_OPTION_ID,
                                    t('general.all')
                                )
                            }
                            label={t('cutOff.deliveryType')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchDeliveryType('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchDeliveryType, setSearchDeliveryType)}
                            {/* Select All Option */}
                            <MenuItem key={ALL_OPTION_ID} value={ALL_OPTION_ID}>
                                <Checkbox checked={isAllSelectedForField('selectedDeliveryType')} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {filteredDeliveryTypeOptions.map((option: any) => (
                                <MenuItem
                                    key={option?.deliveryTypeId}
                                    value={option?.deliveryTypeId}
                                    disabled={isAllSelectedForField('selectedDeliveryType')}
                                >
                                    <Checkbox
                                        checked={
                                            formState?.selectedDeliveryType?.includes(option?.deliveryTypeId) ?? false
                                        }
                                        disabled={isAllSelectedForField('selectedDeliveryType')}
                                    />
                                    <ListItemText primary={option?.deliveryTypeTranslated ?? ''} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Departure Location */}
                    <FormControl className={COSK.fieldDepartureLocation} fullWidth>
                        <InputLabel id="departureLocation-label">{t('cutOff.departureLocation')}</InputLabel>
                        <Select
                            className={COSK.inputSelectDepartureLocation}
                            labelId="departureLocation-label"
                            multiple
                            value={formState?.selectedDepartureLocation ?? []}
                            onChange={handleSelectWithAllChange('selectedDepartureLocation')}
                            renderValue={(selected) => renderSelectValueWithAll(selected, departureLocationOptions)}
                            label={t('cutOff.departureLocation')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchDepartureLocation('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchDepartureLocation, setSearchDepartureLocation)}
                            {/* Select All Option */}
                            <MenuItem key={ALL_OPTION_ID} value={ALL_OPTION_ID}>
                                <Checkbox checked={isAllSelectedForField('selectedDepartureLocation')} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {filteredDepartureLocationOptions.map((option) => (
                                <MenuItem
                                    key={option?.id}
                                    value={option?.id}
                                    disabled={isAllSelectedForField('selectedDepartureLocation')}
                                >
                                    <Checkbox
                                        checked={formState?.selectedDepartureLocation?.includes(option?.id) ?? false}
                                        disabled={isAllSelectedForField('selectedDepartureLocation')}
                                    />
                                    <ListItemText primary={option?.name ?? ''} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Arrival Location */}
                    <FormControl className={COSK.fieldArrivalLocation} fullWidth>
                        <InputLabel id="arrivalLocation-label">{t('cutOff.arrivalLocation')}</InputLabel>
                        <Select
                            className={COSK.inputSelectArrivalLocation}
                            labelId="arrivalLocation-label"
                            multiple
                            value={formState?.selectedArrivalLocation ?? []}
                            onChange={handleSelectWithAllChange('selectedArrivalLocation')}
                            renderValue={(selected) => renderSelectValueWithAll(selected, arrivalLocationOptions)}
                            label={t('cutOff.arrivalLocation')}
                            onClose={() => setSearchArrivalLocation('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchArrivalLocation, setSearchArrivalLocation)}
                            {/* Select All Option */}
                            <MenuItem key={ALL_OPTION_ID} value={ALL_OPTION_ID}>
                                <Checkbox checked={isAllSelectedForField('selectedArrivalLocation')} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {filteredArrivalLocationOptions.map((option) => (
                                <MenuItem
                                    key={option?.id}
                                    value={option?.id}
                                    disabled={isAllSelectedForField('selectedArrivalLocation')}
                                >
                                    <Checkbox
                                        checked={formState?.selectedArrivalLocation?.includes(option?.id) ?? false}
                                        disabled={isAllSelectedForField('selectedArrivalLocation')}
                                    />
                                    <ListItemText primary={option?.name ?? ''} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Active Status */}
                    <FormControl className={COSK.fieldActiveStatus} fullWidth>
                        <InputLabel id="activeStatus-label">{t('cutOff.activeStatus')}</InputLabel>
                        <Select
                            className={COSK.inputSelectActiveStatus}
                            labelId="activeStatus-label"
                            multiple
                            value={formState?.selectedActiveStatus ?? []}
                            onChange={handleSelectWithAllChange('selectedActiveStatus')}
                            renderValue={(selected: string[]) => {
                                if (!selected?.length) return '';
                                if (selected.includes(ALL_OPTION_ID as any)) {
                                    return t('general.all');
                                }
                                return (activeStatusOptions ?? [])
                                    .filter((opt) => opt?.id != null && selected.includes(opt.id))
                                    .map((opt) => opt?.name ?? '')
                                    .join(', ');
                            }}
                            label={t('cutOff.activeStatus')}
                            onClose={() => setSearchActiveStatus('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchActiveStatus, setSearchActiveStatus)}
                            {/* Select All Option */}
                            <MenuItem key={ALL_OPTION_ID} value={ALL_OPTION_ID}>
                                <Checkbox checked={isAllSelectedForField('selectedActiveStatus')} />
                                <ListItemText primary={t('general.all')} />
                            </MenuItem>
                            {filteredActiveStatusOptions.map((option) => (
                                <MenuItem
                                    key={option?.id}
                                    value={option?.id}
                                    disabled={isAllSelectedForField('selectedActiveStatus')}
                                >
                                    <Checkbox
                                        checked={formState?.selectedActiveStatus?.includes(option?.id) ?? false}
                                        disabled={isAllSelectedForField('selectedActiveStatus')}
                                    />
                                    <ListItemText primary={option?.name ?? ''} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Buttons */}
                <Box
                    className={COSK.actionsRow}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        paddingTop: '32px',
                        padding: '12px',
                    }}
                >
                    <SCUIButton
                        className={COSK.inputButtonClear}
                        text={t('general.clear')}
                        onClick={() => setFormState(initialFormState)}
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                    />
                    <SCUIButton
                        className={COSK.inputButtonFilter}
                        text={t('general.filter')}
                        type="primary"
                        sizeType="fixed"
                        disabled={isLoadingOptions}
                        size={'l'}
                        buttonType="button"
                        onClick={handleFilter}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};
